import AdsClick from "@mui/icons-material/AdsClick";
import Assignment from "@mui/icons-material/Assignment";
import Clear from "@mui/icons-material/Clear";
import DeleteOutline from "@mui/icons-material/DeleteOutlined";
import FilterAlt from "@mui/icons-material/FilterAlt";
import ForkLeft from "@mui/icons-material/ForkLeft";
import FormatListBulleted from "@mui/icons-material/FormatListBulleted";
import ListAlt from "@mui/icons-material/ListAlt";
import LocationOn from "@mui/icons-material/LocationOn";
import Refresh from "@mui/icons-material/Refresh";
import Sort from "@mui/icons-material/Sort";
import Stop from "@mui/icons-material/Stop";
import TouchApp from "@mui/icons-material/TouchApp";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { Position, useNodeId } from "@xyflow/react";
import type { PropsWithChildren, ReactNode } from "react";

import type { RuleNodeProps, RuleNodeType } from "../types/nodes";
import AddConnectionHandle from "./AddConnectionHandle";
import { GhostNode } from "./GhostNode";
import { useNodeActions } from "./node-actions-context";
import { BaseNodeBody, BaseNodeContainer, BaseNodeLabelContainer, BaseNodeMainContent } from "./styled/basenode.styled";

type BaseNodeProps = PropsWithChildren<RuleNodeProps>;

type DefaultNodeVisuals = {
  icon: ReactNode;
  color: string;
};

const NODE_DISPLAY_NAMES: Partial<Record<RuleNodeType, string>> = {
	clear_reaction: "Clear Reaction",
	query_builder: "Query Builder",
	reaction_location: "Reaction Location",
	reaction: "Reaction",
	decision: "Decision",
	filter: "Filter",
	loop: "Loop",
	sort: "Sort",
	stop: "Stop",
	assignment: "Assignment",
	event_listener: "Event Listener",
	action: "Action",
	ghost: "Ghost",
};

const getNodeDisplayName = (type: RuleNodeType): string =>
	NODE_DISPLAY_NAMES[type] ?? type;

// Per-node-type colors mapped to the 6-gradient brand palette.
//   `color`     — solid mid-tone (used for borders + glyph color).
//   `gradient`  — linear-gradient string (used for the bottom-right "+"
//                 add button fill + the SVG fill on the icon glyph).
//   `gradId`    — id of the SVG <linearGradient> def to apply to the
//                 icon glyph via `& path { fill: url(#…) }`.
//
// Palette assignments:
//   reaction_location, assignment → primary teal-blue
//   query_builder, decision        → blue
//   loop                           → purple
//   filter                         → orange
//   sort                           → yellow  (the new 6th brand gradient)
//   reaction, clear_reaction, stop → red
//
// Source of truth: src/theme.js `gradient` + `nodeColors`.
const DEFAULT_NODE_ICONS: Record<RuleNodeType, DefaultNodeVisuals & { gradient?: string; gradId?: string }> = {
	reaction_location: { icon: <LocationOn fontSize="small" />,         color: "#06B097", gradient: "linear-gradient(135deg, #02D5AB 0%, #0A7FA8 100%)", gradId: "coapp-grad-primary" },
	query_builder:     { icon: <FormatListBulleted fontSize="small" />, color: "#3F77E8", gradient: "linear-gradient(135deg, #5BA0FF 0%, #2C66E0 100%)", gradId: "coapp-grad-blue" },
	decision:          { icon: <ForkLeft fontSize="small" />,           color: "#3F77E8", gradient: "linear-gradient(135deg, #5BA0FF 0%, #2C66E0 100%)", gradId: "coapp-grad-blue" },
	filter:            { icon: <FilterAlt fontSize="small" />,          color: "#F35A35", gradient: "linear-gradient(135deg, #FF7A3C 0%, #EE3A2D 100%)", gradId: "coapp-grad-orange" },
	sort:              { icon: <Sort fontSize="small" />,               color: "#F5BB35", gradient: "linear-gradient(135deg, #FFD93B 0%, #F2A93B 100%)", gradId: "coapp-grad-yellow" },
	loop:              { icon: <Refresh fontSize="small" />,            color: "#A434D9", gradient: "linear-gradient(135deg, #D63AD9 0%, #8C30E0 100%)", gradId: "coapp-grad-purple" },
	assignment:        { icon: <Assignment fontSize="small" />,         color: "#06B097", gradient: "linear-gradient(135deg, #02D5AB 0%, #0A7FA8 100%)", gradId: "coapp-grad-primary" },
	reaction:          { icon: <ListAlt fontSize="small" />,            color: "#D32E26", gradient: "linear-gradient(135deg, #E94136 0%, #B81E15 100%)", gradId: "coapp-grad-red" },
	clear_reaction:    { icon: <Clear fontSize="small" />,              color: "#D32E26", gradient: "linear-gradient(135deg, #E94136 0%, #B81E15 100%)", gradId: "coapp-grad-red" },
	stop:              { icon: <Stop fontSize="small" />,               color: "#D32E26", gradient: "linear-gradient(135deg, #E94136 0%, #B81E15 100%)", gradId: "coapp-grad-red" },
	event_listener:    { icon: <TouchApp fontSize="small" />,           color: "#3F77E8", gradient: "linear-gradient(135deg, #5BA0FF 0%, #2C66E0 100%)", gradId: "coapp-grad-blue" },
	action:            { icon: <AdsClick fontSize="small" />,           color: "#3F77E8", gradient: "linear-gradient(135deg, #5BA0FF 0%, #2C66E0 100%)", gradId: "coapp-grad-blue" },
	ghost:             { icon: <GhostNode />,                           color: "#FFFFFF" },
};

// Convert a hex color to an rgba string with the given alpha. Used for the
// light-tint icon-tile backgrounds.
const hexToRgba = (hex: string, alpha: number): string => {
	const m = hex.replace("#", "");
	const r = parseInt(m.substring(0, 2), 16);
	const g = parseInt(m.substring(2, 4), 16);
	const b = parseInt(m.substring(4, 6), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Node types that cannot be deleted via the hover affordance.
const UNDELETABLE_NODE_TYPES: ReadonlySet<RuleNodeType> = new Set([
	"reaction_location",
	"ghost",
]);

// Node types that should NOT show the bottom-right "+" add-node action button.
// `stop` is a terminal node — nothing connects after it. `ghost` is a
// placeholder. `decision` renders its own twin bottom-LEFT (false) +
// bottom-RIGHT (true) corner buttons below.
const NO_ADD_ACTION_NODE_TYPES: ReadonlySet<RuleNodeType> = new Set([
	"ghost",
	"stop",
	"decision",
]);

const BaseNode = ({ data, selected, children }: BaseNodeProps) => {
	const visuals = DEFAULT_NODE_ICONS[data.type];
	const { icon: defaultIcon, color: defaultColor, gradient: defaultGradient, gradId: defaultGradId } = visuals;
	const icon = data.icon ?? defaultIcon;
	const iconColor = data.iconColor ?? defaultColor;
	const iconGradient = defaultGradient;
	const iconGradId = defaultGradId;
	const tintBg = hexToRgba(iconColor, 0.14);
	const nodeId = useNodeId();
	const actions = useNodeActions();
	const canDelete = !UNDELETABLE_NODE_TYPES.has(data.type) && actions != null && nodeId != null;
	// NO_ADD_ACTION_NODE_TYPES retained as documentation of which nodes shouldn't
	// receive a downstream add affordance, but `canAdd` is no longer consulted
	// here — the per-node AddConnectionHandle handles its own visibility.

	return (
		<BaseNodeContainer
			sx={{
				// Reveal corner action buttons on:
				//   • mouse hover (regular pointer users)
				//   • keyboard focus anywhere inside the tile (Tab navigation)
				//   • when the node is selected (so users always see them post-add)
				// Last branch satisfies WCAG 2.1 AA 2.1.1 — keyboard users need a
				// visible path to delete and add.
				"&:hover .coapp-node-action, &:focus-within .coapp-node-action, &[data-selected='true'] .coapp-node-action": {
					opacity: 1,
					pointerEvents: "auto",
				},
			}}
			data-selected={selected ? "true" : undefined}
		>
			<BaseNodeMainContent
				elevation={0}
				selected={selected}
				accentColor={iconColor}
			>
				<BaseNodeLabelContainer selected={selected} accentColor={iconColor}>
					{icon && (
						<Box
							sx={{
								color: iconColor,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: 26,
								height: 26,
								borderRadius: 1.25,
								// Light-tinted background (14% of the category color), with the
								// glyph itself filled by the matching SVG <linearGradient>.
								background: tintBg,
								"& > *": { fontSize: "16px !important" },
								...(iconGradId
									? { "& path": { fill: `url(#${iconGradId})` } }
									: {}),
							}}
						>
							{icon}
						</Box>
					)}
					<Typography variant="caption" noWrap sx={{ color: iconColor + " !important" }}>
						{getNodeDisplayName(data.type)}
					</Typography>
				</BaseNodeLabelContainer>

				<BaseNodeBody>
					<Stack spacing={0.75}>
						<Typography
							variant="subtitle1"
							sx={{
								// QA fix (A8): underlined rule name for visibility. Weight
								// dialed back from 800 → 600 per design — 800 read as too
								// heavy against the node card.
								fontWeight: 600,
								fontSize: 17,
								letterSpacing: "-0.01em",
								color: "rgba(13, 31, 48, 0.92)",
								lineHeight: 1.3,
								textDecoration: "underline",
								textDecorationThickness: "1px",
								textUnderlineOffset: "3px",
								textDecorationColor: "rgba(13, 31, 48, 0.25)",
							}}
						>
							{data.name}
						</Typography>
						{children ? <Box>{children}</Box> : null}
					</Stack>
				</BaseNodeBody>

			</BaseNodeMainContent>

			{/* TOP-RIGHT — solid red trash (white glyph). Hover-revealed. */}
			{canDelete && (
				<Tooltip title="Delete node" arrow placement="top">
					<Box
						component="button"
						type="button"
						// QA fix (B1): "nodrag nopan" stops React Flow from swallowing
						// the pointer-down as a node drag, which made the delete button
						// need multiple clicks / sometimes miss entirely. onMouseDown
						// stopPropagation belt-and-suspenders against the same race.
						className="coapp-node-action nodrag nopan"
						onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
							e.stopPropagation();
						}}
						onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
							e.stopPropagation();
							if (nodeId) actions!.onDeleteNode(nodeId);
						}}
						aria-label="Delete node"
						sx={{
							all: "unset",
							position: "absolute",
							top: -10,
							right: -10,
							zIndex: 3,
							width: 26,
							height: 26,
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							borderRadius: "50%",
							cursor: "pointer",
							color: "#FFFFFF",
							background: "#E03030",
							border: "1.5px solid #FFFFFF",
							// 44×44 invisible touch hit area per WCAG 2.5.5 — extends past
							// the visible 26px button without changing the visual.
							"&::after": {
								content: "\"\"",
								position: "absolute",
								top: "50%",
								left: "50%",
								width: 44,
								height: 44,
								transform: "translate(-50%, -50%)",
								borderRadius: "50%",
							},
							opacity: 0,
							pointerEvents: "none",
							transition: "opacity 0.15s ease, transform 0.15s ease, filter 0.15s ease",
							"&:hover": {
								transform: "scale(1.08)",
								filter: "brightness(1.1)"
							},
							"& svg": { fontSize: 14, color: "#FFFFFF" },
							"& svg path": { fill: "#FFFFFF" }
						}}
					>
						<DeleteOutline />
					</Box>
				</Tooltip>
			)}

			{/* Floating "+" below every non-terminal node. Rendered here (outside
			    BaseNodeMainContent) so it sits cleanly below the tile for all
			    node types. Decision, stop, and ghost nodes are excluded. */}
			{!NO_ADD_ACTION_NODE_TYPES.has(data.type) && (
				<AddConnectionHandle
					type="source"
					position={Position.Bottom}
					id="out"
					aria-label="Connect next step"
				/>
			)}
		</BaseNodeContainer>
	);
};

export default BaseNode;
